import { useState, type FormEvent } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "@/lib/seo";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, Notice, Panel, TextAreaField, TextField } from "@/components/system";
import { useAuth } from "@/contexts/AuthContext";
import { useCity } from "@/contexts/CityContext";
import { loginRedirectUri } from "@/lib/keycloak";
import { submitPublicLead } from "@/lib/public-contact";
import { track } from "@/lib/analytics";

const CONTACT_EMAIL = "hello@pulseboard.com.au";

/**
 * Dedicated auth/access shell — no Nav/Footer distraction. New users can
 * submit access details directly; existing users can continue to Keycloak.
 * noindex because this page has no crawl value.
 */
export default function Auth() {
  const { isLoading, isAuthenticated, login, initError } = useAuth();
  const { city, link } = useCity();
  const location = useLocation();
  const requestedPath =
    location.state &&
    typeof location.state === "object" &&
    "from" in location.state &&
    typeof location.state.from === "string" &&
    location.state.from.startsWith("/board")
      ? location.state.from
      : link("/board");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [message, setMessage] = useState("");
  const [leadStatus, setLeadStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleAccessRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLeadStatus("submitting");

    try {
      await submitPublicLead(city.slug, {
        intent: "request_access",
        email,
        name: name || undefined,
        organisation: organisation || undefined,
        message: message || undefined,
        sourcePath: `${window.location.pathname}${window.location.search}`,
      });
      track("access_request", { intent: "request_access" });
      setLeadStatus("success");
      setEmail("");
      setName("");
      setOrganisation("");
      setMessage("");
    } catch {
      setLeadStatus("error");
    }
  };

  if (isLoading) {
    return (
      <FocusShell>
        <p className="ds-small" role="status">Checking your session…</p>
      </FocusShell>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={requestedPath} replace />;
  }

  return (
    <FocusShell>
      <SEO
        title="Sign in · PulseBoard"
        description="Sign in to PulseBoard to manage referrals, availability, or your specialist profile."
        path="/auth"
        noindex
      />
      <Panel aria-labelledby="signin-heading">
        <div className="ds-stack" style={{ gap: 8 }}>
          <p className="ds-eyebrow ds-eyebrow--teal">Members</p>
          <h1 id="signin-heading" className="ds-display-2">Sign in to PulseBoard</h1>
          <p className="ds-small">Open your private {city.name} clinician Board.</p>
        </div>
        <Button
          size="lg"
          block
          disabled={!!initError}
          onClick={() => void login({ redirectUri: loginRedirectUri(requestedPath) })}
        >
          Continue to sign in
          <ArrowRight aria-hidden="true" />
        </Button>
        {initError ? <Notice tone="error">Sign-in is temporarily unavailable. The contact form below still works.</Notice> : null}
        <p className="ds-small">
          Not a member yet? <Link className="ds-link" to={link("/join")}>Join PulseBoard</Link>
        </p>
      </Panel>

      <Panel aria-labelledby="contact-heading">
        <div className="ds-stack" style={{ gap: 8 }}>
          <h2 id="contact-heading" className="ds-title">Request access or get in touch</h2>
          <p className="ds-small">For pilot access or product questions. We reply directly.</p>
        </div>
        {leadStatus === "success" ? (
          <Notice tone="success">Thank you. Your request has been received.</Notice>
        ) : (
          <form className="ds-stack" style={{ gap: 14 }} onSubmit={handleAccessRequest}>
            <TextField label="Email" type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@practice.com.au" />
            <div className="ds-form-grid">
              <TextField label="Name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Optional" />
              <TextField label="Practice or organisation" autoComplete="organization" value={organisation} onChange={(event) => setOrganisation(event.target.value)} placeholder="Optional" />
            </div>
            <TextAreaField label="Message" value={message} onChange={(event) => setMessage(event.target.value)} rows={3} placeholder="Optional" />
            {leadStatus === "error" ? (
              <Notice tone="error">The request didn't send. Try again, or email <span className="ds-num">{CONTACT_EMAIL}</span>.</Notice>
            ) : null}
            <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
              <p className="ds-fine">
                By sending this you agree to the <Link className="ds-link" to={link("/privacy")}>Privacy Policy</Link> and{" "}
                <Link className="ds-link" to={link("/terms")}>Terms of Service</Link>.
              </p>
              <Button type="submit" variant="secondary" disabled={leadStatus === "submitting"}>
                {leadStatus === "submitting" ? "Sending…" : "Send request"}
              </Button>
            </div>
          </form>
        )}
      </Panel>
    </FocusShell>
  );
}
